import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router';
import {
  getIncomers, getOutgoers, isNode
} from 'react-flow-renderer';

import { WorkflowWithStepsAndEdges } from 'src/types';
import useGetRequest from 'src/hooks/useGetRequest';
import * as api from 'src/utils/api';
import { workflowToElements } from 'src/views/shared/flowProcess';
import {
  ADD_TRIGGER, ADD_ACTION, ACTION_EDGE, START_PROCESS, IS_FOCUSED
} from 'src/views/shared/Process/constants';

import RightDrawer from './RightDrawer';
import ProcessEditor from './ProcessEditor';
import LeftDrawer from './LeftDrawer';

export const initializeDummyActionNode = () => ({
  id: uuidv4(),
  type: ADD_ACTION,
  draggable: false,
  data: {},
});

export const initializeDummyTriggerNode = () => ({
  id: uuidv4(),
  type: ADD_TRIGGER,
  draggable: false,
  data: {},
});

export const initializeDummyEdge = (fromNodeId: string, toNodeId: string) => ({
  id: uuidv4(),
  source: fromNodeId,
  target: toNodeId,
  type: ACTION_EDGE,
  animated: true,
});

export const focusElements = (elements: any[], selectedElementId?: string) => elements.map(
  (element: any) => {
    if (isNode(element) && element.id === selectedElementId) {
      return {
        ...element,
        data: {
          ...element.data,
          nodeStyle: IS_FOCUSED
        },
      };
    }
    return element;
  }
);


const hydrateElementsWithExtenders = (elements: any[]) => {
  const nodes = elements.filter((element) => element.data);
  const edges = elements.filter((element) => !element.data);

  // find nodes that don't have outgoing edges
  const nodeIdsWithOutgoingEdges = edges.map((edge) => edge.source);
  // for each node without an outgoing edge, initialize a plus node a few pixels below it and a connector to that node
  const extensionNodes: any = [];
  const extensionEdges: any = [];
  const nodesWithoutOutgoingEdges = nodes.filter((node) => !nodeIdsWithOutgoingEdges.includes(node.id));
  nodesWithoutOutgoingEdges.forEach((node) => {
    const extensionNode = initializeDummyActionNode();
    const extensionEdge = initializeDummyEdge(node.id, extensionNode.id);
    extensionNodes.push(extensionNode);
    extensionEdges.push(extensionEdge);
  });

  const startProcessNode = nodes.find((node) => node.type === START_PROCESS);
  const startProcessHasIncomingNode = Boolean(edges.filter((edge) => edge.target === startProcessNode.id).length);
  if (!startProcessHasIncomingNode) {
    const extensionNode = initializeDummyTriggerNode();
    const extensionEdge = initializeDummyEdge(extensionNode.id, startProcessNode.id);
    extensionNodes.push(extensionNode);
    extensionEdges.push(extensionEdge);
  }

  return [...nodes, ...edges, ...extensionNodes, ...extensionEdges];
};

const Process = () => {
  const { workflowId } = useParams();
  const [selectedElementId, setSelectedElementId] = React.useState<string>();

  const [elements, setElements] = React.useState([]);

  // eslint-disable-next-line no-use-before-define
  const deleteStep = (stepId: string) => api.deleteStep(workflowId, stepId).then(() => refreshWorkflow());
  const forkStep = (stepId: string) => {
    const dummy = initializeDummyActionNode();
    const edge = initializeDummyEdge(stepId, dummy.id);
    // @ts-ignore
    setElements((prevElements) => [...prevElements, dummy, edge]);
    setSelectedElementId(dummy.id);
  };

  const nodeProps = {
    deleteStep,
    forkStep,
  };

  const {
    result: workflow,
    isLoading,
    httpRequest: refreshWorkflow,
  }: {
    result: WorkflowWithStepsAndEdges,
    isLoading: boolean,
    httpRequest: () => void,
  } = useGetRequest({
    url: `/automatic/workflows/${workflowId}/`,
    initialResult: { steps: [], edges: [] },
    // @ts-ignore
    onSuccess: (wf: WorkflowWithStepsAndEdges) => setElements(hydrateElementsWithExtenders(workflowToElements(wf, nodeProps))),
  });
  const createEdge = (fromStepId: string, toStepId: string) => api.createEdge(workflowId, { fromStepId, toStepId }).then(() => refreshWorkflow());

  const selectedElement = elements.find((element: any) => element.id === selectedElementId);
  const selectedElementIncomers = selectedElement ? getIncomers(selectedElement, elements) : [];
  const selectedElementOutgoers = selectedElement ? getOutgoers(selectedElement, elements) : [];

  return (
    <>
      <LeftDrawer
        workflowId={workflowId}
      />
      <ProcessEditor
        createEdge={createEdge}
        setElements={setElements}
        elements={elements}
        isLoading={isLoading && !workflow.steps.length}
        getWorkflow={refreshWorkflow}
        selectedElementId={selectedElementId}
        setSelectedElementId={setSelectedElementId}
      />
      <RightDrawer
        workflow={workflow}
        selectedElement={selectedElement}
        selectedElementIncomers={selectedElementIncomers}
        selectedElementOutgoers={selectedElementOutgoers}
        workflowId={workflowId}
        refreshWorkflow={refreshWorkflow}
      />
    </>
  );
};

export default Process;
